import dayjs from "dayjs";
import holidayJp from "@holiday-jp/holiday_jp";
import { AuthorityUUID, hasPermission, WhiteBoardPermission } from "../../../declaration/Permissions";
import { getLoginName, getLoginUuid } from "../../../utils/quotationInvoice/getLoginName";
import { getCompanyUuid } from "../../../utils/getCompanyUuid";
import { employeeSearch } from "../../../services/bukken/employeeService";

export const SCHEDULE_MODE = {
  PROPERTY: "property",
  WORKER: "worker"
};

export const WORKER_MODE = {
  LIST: "list",
  GRID: "grid"
};

export const WORKER_TYPE = {
  INTERNAL: 1,
  EXTERNAL: 2
};

export const SCHEDULE_TYPE = {
  PROJECT: "project",
  WORKER: "worker"
};

export const genPayload = (payload) => ({
  data: {
    info: payload
  }
});

export const ENV_INFO = {
  START_DAY_OF_WEEK: "week_start",
  TIME_RANGE: "default_time", // 初期作業時間（時間指定モードの初期値）
  TIME_ALLDAY: "day_all", // 終日
  TIME_AM: "day_am", // 午前
  TIME_PM: "day_pm", // 午後
  WHITEBOARD: "Whiteboard"
};

export const ASSIGN_TIME_TYPE = {
  CUSTOM: 0, // 時間指定（旧 PART_DAY）
  PART_DAY: 0, // 後方互換のための別名
  ALL_DAY: 1, // 終日
  AM: 2, // 午前
  PM: 3 // 午後
};

// 終日/午前/午後 のデフォルトプリセット時刻（設定未保存時に使用）
export const DEFAULT_TIME_PRESETS = {
  allday: { startTime: "00:00", endTime: "23:59" },
  am: { startTime: "00:00", endTime: "12:00" },
  pm: { startTime: "12:00", endTime: "23:59" }
};

export const UPDATED_STEP = {
  NOT_UPDATED: 0,
  UPDATED_START_DAY_OF_WEEK: 1,
  UPDATED_DATE_RANGE: 2
};

export const PENDING_MODAL_TYPE = {
  outOfPeriod: "outOfPeriod",
  duplicate: "duplicate"
};

export const WHITEBOARD_MESSAGES = {
  CREATE_SUCCESS: "登録に成功しました。",
  CREATE_FAIL: "登録に失敗しました。",
  UPDATE_SUCCESS: "更新に成功しました。",
  UPDATE_FAIL: "更新に失敗しました。",
  DELETE_SUCCESS: "削除しました。",
  DELETE_FAIL: "削除に失敗しました。"
};

// date: yyyy-mm-dd, time: HH:mm
// return: yyyy-mm-ddTHH:mm:00
export const timeAndDateToDateTime = (date, time) => {
  return dayjs(`${date}T${time}:00`).format("YYYY-MM-DDTHH:mm:ss");
};

// startDate: yyyy-mm-dd, endDate: yyyy-mm-dd
// options: { includeSaturday: boolean, includeSunday: boolean, includeHolidays: boolean }
// return: array of yyyy-mm-dd
export const getAssignDates = (startDate, endDate, options = {}) => {
  const { includeSaturday = false, includeSunday = false, includeHolidays = false } = options;

  // Build holiday set only if needed
  let holidaySet = null;
  if (!includeHolidays) {
    holidaySet = new Set(
      holidayJp
        .between(dayjs(startDate).toDate(), dayjs(endDate).toDate())
        .map((d) => dayjs(d.date).format("YYYY-MM-DD"))
    );
  }

  const result = [];
  let current = dayjs(startDate);
  const end = dayjs(endDate);

  while (current.isBefore(end) || current.isSame(end, "day")) {
    const dateStr = current.format("YYYY-MM-DD");
    const day = current.day(); // 0 = Sun, 6 = Sat

    const isSaturday = day === 6;
    const isSunday = day === 0;
    const isHoliday = holidaySet?.has(dateStr);

    // Apply filters
    if ((includeSaturday || !isSaturday) && (includeSunday || !isSunday) && (includeHolidays || !isHoliday)) {
      result.push(dateStr);
    }

    current = current.add(1, "day");
  }

  return result;
};

export const defaultItemPermission = async () => {
  const employees = await employeeSearch();
  const currentEmployee = employees?.find((item) => item.user_uuid === getLoginUuid());

  return {
    construct_manager_uuids: hasPermission(WhiteBoardPermission.EDIT, AuthorityUUID.Whiteboard)
      ? [
          {
            employee_name: currentEmployee?.name || getLoginName(),
            employee_uuid: currentEmployee?.employee_uuid || getLoginUuid()
          }
        ]
      : [],
    bukken_customer_employee_uuids: !hasPermission(WhiteBoardPermission.EDIT, AuthorityUUID.Whiteboard)
      ? [
          {
            employee_name: currentEmployee?.name || getLoginName(),
            employee_uuid: currentEmployee?.employee_uuid || getLoginUuid(),
            type: 0
          }
        ]
      : []
  };
};

export const getDateRange = (currentDate, startDayOfWeek) => {
  const d = dayjs(currentDate);
  const offset = (d.day() - startDayOfWeek + 7) % 7;
  const firstDateOfRange = d.subtract(offset, "day").format("YYYY-MM-DD");

  return {
    start: dayjs(firstDateOfRange).toDate(),
    currentDate,
    end: dayjs(firstDateOfRange).add(6, "day").toDate()
  };
};
